import type {
    GeneratedInsight,
    Status,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import {
    AiEnhancedAssetType,
    InsightOutputFormat,
    InsightType,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { useAsync } from "@react-hookz/web";
import { isEmpty } from "lodash";
import React, { useCallback, useMemo } from "react";
import { useStore } from "zustand";

import { getBlinkApiClientV2 } from "../../../../../api/blinkApi/blinkApi";
import { useAsyncErrorStatusNotification } from "../../../../../hooks/notifications/useAsyncErrorStatusNotification";
import { authStore } from "../../../../../store/auth";
import { FeatureFlagsStore } from "../../../../../store/featureFlags";
import { globalWorkbenchStore } from "../../../../../store/globalWorkbench";
import { reportIssueStore } from "../../../../../store/reportIssue";
import { workbenchStore } from "../../../../../store/workbench";
import { AquilaThumbsFeedbackPlacement } from "../../../../../types/aquila";
import { AquilaReportIssueTriggerType } from "../../../../../types/aquila";
import type { CardDetail } from "../../../../../types/card";
import type { DownloadAsset } from "../../../../../types/common";
import type { PackagedInsightAiMetadataAttributes } from "../../../../../types/packagedInsight";
import { PackagedInsightAiDeckSize } from "../../../../../types/packagedInsight";
import { Progress } from "../../../../../types/progress";
import { shortenedInsightTypeLookup } from "../../../../../utils/aiEnhancedInsights.utils";
import { isLoading } from "../../../../../utils/api.utils";
import { isSuccess } from "../../../../../utils/generatedInsights.utils";
import { logViewInsightDetailsClick } from "../../../../../utils/telemetry.utils";
import { AquilaThumbsFeedback } from "../../../../aquilaThumbsFeedback/AquilaThumbsFeedback";
import {
    DownloadErrorNotification,
    PreviewErrorNotification,
} from "../../../../common/ErrorNotificationWithLink";
import { StyledActionsContainer } from "../../../../common/styled.components";
import { InsightSelectionActionText } from "../../InsightSelectionActionText";
import { ArtifactGenerationFailedAction } from "../ArtifactGenerationFailedAction";
import { PkgVisualizationFailedAction } from "../PkgVisualizationFailedAction";
import { VisualizationSuccessAction } from "../VisualizationSuccessAction";

import { useSetPackagedInsightDetails } from "./useSetPackagedInsightDetails";

export interface PackagedInsightCardActionProps {
    executionId: string;
    advertiserId: string;
    title: string;
    details: CardDetail[];
    progress: Progress;
    description?: string;
    aiEnhancedStatus?: Status;
    index?: number;
    insight: GeneratedInsight;
    aiMetadata?: Record<PackagedInsightAiMetadataAttributes, string>;
}

export const PackagedInsightCardAction: React.FunctionComponent<
    PackagedInsightCardActionProps
> = ({
    executionId,
    advertiserId,
    progress,
    title,
    details,
    description,
    aiEnhancedStatus,
    index,
    insight,
    aiMetadata,
}) => {
    const [thumbsFeedbackCard] = useStore(FeatureFlagsStore, (state) => [
        state.thumbsFeedbackCard,
    ]);

    const [getSlidesSelected] = useStore(globalWorkbenchStore, (state) => [
        state.getSlidesSelected,
    ]);

    const [getSlideIndexes] = useStore(workbenchStore, (state) => [
        state.getSlideIndexes,
    ]);
    const [setReportIssueContextData, openModal] = useStore(
        reportIssueStore,
        (state) => [state.setReportIssueContextData, state.openModal],
    );

    const [previewAsset, setPreviewAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);
    const [downloadAsset, setDownloadAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);

    const [requesterAlias] = useStore(authStore, (state) => [state.username]);

    const aiDeckLength = aiMetadata?.deckSize;

    const identifier: string =
        isSuccess(aiEnhancedStatus) &&
        aiDeckLength !== PackagedInsightAiDeckSize.FULL_LENGTH
            ? `shortened-${executionId}`
            : executionId;

    const sourceInsightType: InsightType = InsightType.IHUB;
    const insightType: InsightType = isSuccess(aiEnhancedStatus)
        ? shortenedInsightTypeLookup[sourceInsightType]
        : sourceInsightType;

    const disabled = Object.values({
        executionId,
        advertiserId,
    }).some((val) => isEmpty(val));

    const hasRelevantInsightsDeck: boolean = useMemo(() => {
        return downloadAsset?.hasRelevantInsightsDeck ?? false;
    }, [downloadAsset]);

    const [downloadState, downloadAction] = useAsync(async () => {
        const data = await (
            await getBlinkApiClientV2()
        ).getPackagedInsightDownload({
            requesterAlias,
            executionId,
            advertiserId,
            aiEnhancedAssetType: isSuccess(aiEnhancedStatus)
                ? AiEnhancedAssetType.CUSTOMIZED_PACKAGE_DECK
                : undefined,
        });
        setDownloadAsset(data as DownloadAsset);
    });

    useAsyncErrorStatusNotification(
        downloadState,
        [],
        {
            Error: (
                <DownloadErrorNotification
                    onReportClick={() =>
                        openModal(AquilaReportIssueTriggerType.SIDE_PANEL)
                    }
                />
            ),
        },
        identifier,
        (error) => {
            setReportIssueContextData({
                ...insight,
                executionId,
                apiError: {
                    requestUrl: "getGeneratedInsightDownload API (download)",
                    errorResponse: error.message || JSON.stringify(error),
                    errorCode: error.statusCode || error.code || "Unknown",
                },
            });
        },
    );

    const [previewState, previewAction] = useAsync(async () => {
        const data = await (
            await getBlinkApiClientV2()
        ).getPackagedInsightDownload({
            requesterAlias,
            executionId,
            advertiserId,
            outputFormat: InsightOutputFormat.PDF,
            aiEnhancedAssetType: isSuccess(aiEnhancedStatus)
                ? AiEnhancedAssetType.CUSTOMIZED_PACKAGE_DECK
                : undefined,
        });
        setPreviewAsset(data as DownloadAsset);
    });

    useAsyncErrorStatusNotification(
        previewState,
        [],
        {
            Error: (
                <PreviewErrorNotification
                    onReportClick={() =>
                        openModal(AquilaReportIssueTriggerType.SIDE_PANEL)
                    }
                />
            ),
        },
        identifier,
        (error) => {
            setReportIssueContextData({
                ...insight,
                executionId,
                apiError: {
                    requestUrl: "getGeneratedInsightDownload API (preview)",
                    errorResponse: error.message || JSON.stringify(error),
                    errorCode: error.statusCode || error.code || "Unknown",
                },
            });
        },
    );

    const handleInsightDetails = useCallback(async (): Promise<void> => {
        logViewInsightDetailsClick(
            identifier,
            (index ?? 0) + 1,
            undefined,
            insightType,
        );
        void previewAction.execute();
        void downloadAction.execute();
        setReportIssueContextData({ ...insight, executionId });
        setPackagedInsightDetails();
    }, [identifier, index, executionId, insight]);

    const setPackagedInsightDetails = useSetPackagedInsightDetails({
        executionId,
        identifier,
        insightType,
        sourceInsightType,
        insight,
        description,
        details,
        title,
        previewAsset,
        downloadAsset,
        previewLoading: isLoading(previewState.status),
        downloadLoading: isLoading(downloadState.status),
        requesterAlias,
        advertiserId,
        aiEnhancedStatus,
        hasRelevantInsightsDeck,
        reset: () => {
            setPreviewAsset(undefined);
            setDownloadAsset(undefined);
        },
    });

    const numSelectedSlides = getSlidesSelected(identifier).length;

    return (
        <StyledActionsContainer>
            {(() => {
                switch (progress) {
                    case Progress.ARTIFACTS_GENERATION_FAILED:
                        return (
                            <ArtifactGenerationFailedAction
                                executionId={executionId}
                            />
                        );
                    case Progress.VISUALIZATION_FAILED:
                        return (
                            <PkgVisualizationFailedAction
                                executionId={executionId}
                            />
                        );
                    default:
                        return (
                            <VisualizationSuccessAction
                                executionId={executionId}
                                disabled={disabled}
                                handleInsightDetails={handleInsightDetails}
                            />
                        );
                }
            })()}
            {numSelectedSlides > 0 && (
                <InsightSelectionActionText
                    identifier={identifier}
                    numSelectedSlides={numSelectedSlides}
                />
            )}
            {numSelectedSlides === 0 && thumbsFeedbackCard && (
                <AquilaThumbsFeedback
                    identifier={identifier}
                    insightType={insightType}
                    placement={AquilaThumbsFeedbackPlacement.CARD}
                />
            )}
        </StyledActionsContainer>
    );
};
